import { useEffect, useRef } from 'react';
import { logger } from './LoggerService';

interface AccessLike {
  component: string;
  level?: string | null;
}

export interface PlanSnapshot {
  accesses?: ReadonlyArray<AccessLike>;
}

interface AccessChange {
  component: string;
  from: string | null | 'absent';
  to: string | null | 'absent';
}

function diffAccesses(
  prev: PlanSnapshot['accesses'],
  next: PlanSnapshot['accesses'],
): AccessChange[] {
  const prevMap = new Map<string, string | null>();
  (prev ?? []).forEach((a) => prevMap.set(a.component, a.level ?? null));
  const seen = new Set<string>();
  const changes: AccessChange[] = [];
  (next ?? []).forEach((a) => {
    seen.add(a.component);
    const before = prevMap.has(a.component)
      ? (prevMap.get(a.component) as string | null)
      : 'absent';
    const after = a.level ?? null;
    if (before !== after) {
      changes.push({ component: a.component, from: before, to: after });
    }
  });
  prevMap.forEach((level, component) => {
    if (!seen.has(component)) {
      changes.push({ component, from: level, to: 'absent' });
    }
  });
  return changes;
}

// Logs company-plan access transitions to the debug-mode logger. Access
// changes are normally invisible mid-session — `useWhoamiQuery` only refetches
// on Profile / User-mgmt edits, login, or SW activation, so a downgrade made
// by a system admin only surfaces on the next natural refetch. Wiring this
// hook at the layout level gives QA a timestamped marker when the FE picks
// the new plan up, which is the signal sub-task 49's lock UX hangs off of.
//
// `CompanyPlan.kind` used to be tracked here too, but the backend now
// restricts that field to system-admin sessions, so admin/user apps (the only
// consumers of this hook) can no longer read it.
//
// Loose `string` typing on `level` is deliberate: each app generates its own
// copy of `AccessLevelEnum`, and TypeScript treats them as nominally distinct
// types even though the runtime values are identical. Keeping the snapshot
// loose lets the same hook serve both shells.
export default function usePlanTransitionLogger(snapshot: PlanSnapshot): void {
  const prevRef = useRef<PlanSnapshot | null>(null);
  const { accesses } = snapshot;

  useEffect(() => {
    if (!accesses) return;
    const prev = prevRef.current;

    if (prev === null) {
      logger.debug('Plan: initial snapshot', {
        'plan.access.count': accesses.length,
      });
      prevRef.current = { accesses };
      return;
    }

    const changes = diffAccesses(prev.accesses, accesses);
    if (changes.length > 0) {
      logger.info('Plan: access levels changed', {
        'plan.access.changes': JSON.stringify(changes),
      });
    }

    prevRef.current = { accesses };
  }, [accesses]);
}
